import React, { useState } from 'react';
import { Heart, Building2, Landmark, Mail, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useSiteContent } from '../context/SiteContentContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RZP_KEY = process.env.REACT_APP_RAZORPAY_KEY_ID;

const PRESET_AMOUNTS = [500, 1000, 2000, 5000];

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

const DonateWidget = () => {
  const [selected, setSelected] = useState(1000);
  const [custom, setCustom] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const amount = custom ? parseInt(custom, 10) : selected;

  const handleDonate = async () => {
    if (!amount || amount < 1) { setError('Please enter a valid amount.'); return; }
    setError('');
    setLoading(true);
    const ok = await loadRazorpayScript();
    if (!ok) { setError('Could not load payment gateway. Please try again.'); setLoading(false); return; }
    try {
      const { data } = await axios.post(`${API}/payments/create-order`, {
        amount: amount * 100,
        currency: 'INR',
        name,
        email,
      });
      const options = {
        key: RZP_KEY,
        amount: data.amount,
        currency: data.currency,
        name: 'Equaltoo Foundation',
        description: 'Donation',
        order_id: data.order_id,
        prefill: { name, email },
        theme: { color: '#003366' },
        handler: async (response) => {
          try {
            await axios.post(`${API}/payments/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            setSuccess(true);
          } catch {
            setError('Payment received but verification failed. Please contact us.');
          }
        },
        modal: { ondismiss: () => setLoading(false) },
      };
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', () => {
        setError('Payment failed. Please try again.');
        setLoading(false);
      });
      rzp.open();
    } catch {
      setError('Could not initiate payment. Please try again.');
    }
    setLoading(false);
  };

  if (success) {
    return (
      <div className="rounded-2xl border border-[#7CA982]/40 bg-[#7CA982]/10 p-6 text-center" data-testid="donate-success">
        <p className="font-heading font-bold text-[#003366] text-lg">Thank you for your donation!</p>
        <p className="text-sm text-[#666] mt-2">Your contribution helps us support more professionals.</p>
        <button className="mt-4 text-sm text-[#E74C3C] underline" onClick={() => { setSuccess(false); setCustom(''); setSelected(1000); }}>Donate again</button>
      </div>
    );
  }

  return (
    <div className="rounded-[1.75rem] border border-[#EAE5E0] bg-white p-6" data-testid="donate-widget">
      <p className="font-heading font-semibold text-[#1A1A1A] mb-4">Choose an amount (₹)</p>
      <div className="grid grid-cols-4 gap-2 mb-4">
        {PRESET_AMOUNTS.map((a) => (
          <button
            key={a}
            data-testid={`preset-amount-${a}`}
            onClick={() => { setSelected(a); setCustom(''); }}
            className={`rounded-xl border py-2.5 text-sm font-semibold transition-colors ${
              !custom && selected === a
                ? 'bg-[#003366] text-white border-[#003366]'
                : 'border-[#EAE5E0] text-[#1A1A1A] hover:border-[#003366]'
            }`}
          >
            ₹{a.toLocaleString('en-IN')}
          </button>
        ))}
      </div>
      <input
        type="number"
        placeholder="Or enter custom amount"
        value={custom}
        onChange={(e) => setCustom(e.target.value)}
        className="w-full rounded-xl border border-[#EAE5E0] px-4 py-2.5 text-sm mb-4 focus:outline-none focus:border-[#003366]"
        data-testid="custom-amount-input"
      />
      <input
        type="text"
        placeholder="Your name (optional)"
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="w-full rounded-xl border border-[#EAE5E0] px-4 py-2.5 text-sm mb-3 focus:outline-none focus:border-[#003366]"
        data-testid="donor-name-input"
      />
      <input
        type="email"
        placeholder="Email for receipt (optional)"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="w-full rounded-xl border border-[#EAE5E0] px-4 py-2.5 text-sm mb-4 focus:outline-none focus:border-[#003366]"
        data-testid="donor-email-input"
      />
      {error && <p className="text-sm text-[#E74C3C] mb-3">{error}</p>}
      <Button
        onClick={handleDonate}
        disabled={loading}
        data-testid="donate-pay-btn"
        className="w-full rounded-full bg-[#E74C3C] hover:bg-[#C0392B] text-white py-6 font-semibold text-base"
      >
        {loading ? 'Opening payment…' : `Donate ₹${(amount || 0).toLocaleString('en-IN')}`}
      </Button>
      <p className="text-xs text-[#999] text-center mt-3">Secured by Razorpay · Supports UPI, cards & net banking</p>
    </div>
  );
};

const Row = ({ label, value }) => (
  value ? (
    <div className="flex justify-between gap-4 py-2.5 border-b border-white/10 last:border-0">
      <span className="text-white/60 text-sm">{label}</span>
      <span className="font-medium text-right">{value}</span>
    </div>
  ) : null
);

const Donate = () => {
  const { donate: c } = useSiteContent();

  return (
    <div className="bg-[#FDFBF9]" data-testid="donate-page">
      {/* Hero */}
      <section className="relative bg-[#003366] text-white overflow-hidden">
        <div className="absolute -bottom-24 -right-16 w-80 h-80 rounded-full bg-white/10 blur-3xl" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center relative">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-white/15 border border-white/20 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest mb-6">
              <Heart className="w-4 h-4" /> Donate
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">{c.hero.title}</h1>
            <p className="mt-6 text-lg text-white/90 max-w-xl leading-relaxed">{c.hero.subtext}</p>
          </div>
          {c.image ? <img src={c.image} alt="Support Equaltoo" className="rounded-[2rem] shadow-2xl w-full h-[320px] lg:h-[400px] object-cover" loading="eager" decoding="async" /> : null}
        </div>
      </section>

      <section className="py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-6 grid lg:grid-cols-2 gap-8 items-start">
          {/* Intro + tax */}
          <div>
            <p className="text-lg text-[#444] leading-relaxed">{c.intro}</p>
            <div className="mt-6 flex items-start gap-3 rounded-2xl bg-white border border-[#EAE5E0] p-5">
              <ShieldCheck className="w-6 h-6 text-[#7CA982] flex-shrink-0 mt-0.5" />
              <p className="text-sm text-[#666]">{c.taxNote}</p>
            </div>
            <div className="mt-6">
              <DonateWidget />
            </div>
          </div>

          {/* Bank details */}
          <div className="rounded-[1.75rem] bg-[#003366] text-white p-8 relative overflow-hidden">
            <div className="absolute -top-16 -right-10 w-40 h-40 rounded-full bg-[#E74C3C]/25 blur-2xl" />
            <div className="flex items-center gap-3 mb-5 relative">
              <Landmark className="w-6 h-6 text-[#E74C3C]" />
              <h2 className="font-heading text-xl font-bold">{c.bank.heading}</h2>
            </div>
            <div className="relative">
              <Row label="Account Name" value={c.bank.accountName} />
              <Row label="Account Number" value={c.bank.accountNumber} />
              <Row label="Bank" value={c.bank.bankName} />
              <Row label="IFSC" value={c.bank.ifsc} />
              <Row label="Branch" value={c.bank.branch} />
              <Row label="UPI" value={c.bank.upi} />
            </div>
          </div>
        </div>
      </section>

      {/* Corporate */}
      <section className="py-16 bg-white border-t border-[#EAE5E0]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="w-14 h-14 rounded-2xl bg-[#003366]/5 flex items-center justify-center mx-auto mb-6">
            <Building2 className="w-7 h-7 text-[#003366]" />
          </div>
          <h2 className="font-heading text-3xl font-bold text-[#1A1A1A]">{c.corporateHeading}</h2>
          <p className="mt-4 text-[#666] leading-relaxed">{c.corporateText}</p>
          <a href={`mailto:${c.contactEmail}`}>
            <Button className="mt-6 rounded-full bg-[#E74C3C] hover:bg-[#C0392B] text-white px-8 py-6">
              <Mail className="w-4 h-4 mr-2" /> {c.contactEmail}
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default Donate;
